import { AnalyticsChartCard } from '@/components/ui/analytics/AnalyticsChartCard';
import { SummaryMetricCard } from '@/components/ui/analytics/SummaryMetricCard';
import { Badge } from '@/components/ui/shadcn/badge';
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from '@/components/ui/shadcn/chart';
import { useChartHeightClass } from '@/hooks/use-chart-height';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { categoricalColor, WORK_ORDER_STATUS_COLORS as STATUS_COLORS, statusColor } from '@/lib/chart-colors';
import { settingLabel } from '@/lib/status-labels';
import type { AnalyticsDateRange, OverdueWorkOrder, StatusCountRow, WorkOrdersAnalyticsData } from '@/types/analytics';
import { CheckCircle2, ClipboardList, Clock3, ExternalLink, Timer, TrendingUp } from 'lucide-react';
import { useMemo } from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, XAxis, YAxis } from 'recharts';

interface Props {
    statusCounts: Record<string, number>;
    financials: { total_cost: number; total_orders: number; completed_orders: number };
    overdueOrders: OverdueWorkOrder[];
    /** Real total of overdue work orders. `overdueOrders` is a capped preview list, so its length understates the true figure. */
    overdueCount?: number;
    workOrdersAnalytics?: WorkOrdersAnalyticsData;
    dateRange?: AnalyticsDateRange;
}

export default function WorkOrdersAnalytics({ financials, overdueOrders, overdueCount: overdueCountProp, workOrdersAnalytics, dateRange }: Props) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const trendHeightClass = isMobile ? 'h-[240px]' : 'h-[320px]';
    const companyStatusHeightClass = useChartHeightClass('lg');
    const truncateCompanyTick = (v: string) => (v.length > 8 ? `${v.slice(0, 8)}…` : v);

    const statusLabel = (status: string) => settingLabel(t, 'work_order_statuses', status);

    const kpis = useMemo(() => {
        const created = Number(financials?.total_orders ?? 0);
        const completed = Number(financials?.completed_orders ?? 0);
        const completionRate = created > 0 ? (completed / created) * 100 : 0;
        const avgResolutionDays = Number(workOrdersAnalytics?.avg_completion_time_days ?? 0);
        const onTimeRate = Number(workOrdersAnalytics?.on_time_rate ?? 0);
        const avgDaysLate = Number(workOrdersAnalytics?.avg_days_late ?? 0);
        return { created, completed, completionRate, avgResolutionDays, onTimeRate, avgDaysLate };
    }, [financials, workOrdersAnalytics]);

    // Prefer the backend's real total; fall back to the (capped) preview list length only if it wasn't provided.
    const overdueCount = overdueCountProp ?? (overdueOrders ?? []).length;
    const overdueUrl = `/work-orders?overdue=true${dateRange ? `&from_date=${dateRange.from_date}&to_date=${dateRange.to_date}` : ''}`;

    const createdVsCompleted = workOrdersAnalytics?.created_vs_completed_trend ?? [];

    const statusPieData = useMemo(
        () =>
            (workOrdersAnalytics?.distribution_by_status ?? []).map((row: StatusCountRow) => ({
                name: statusLabel(row.status),
                value: Number(row.count ?? 0),
                fill: statusColor(STATUS_COLORS, row.status),
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [workOrdersAnalytics],
    );

    const statusChartConfig = useMemo(() => {
        const cfg: Record<string, { label: string; color: string }> = {};
        (workOrdersAnalytics?.distribution_by_status ?? []).forEach((row: StatusCountRow) => {
            cfg[statusLabel(row.status)] = { label: statusLabel(row.status), color: statusColor(STATUS_COLORS, row.status) };
        });
        return cfg;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workOrdersAnalytics]);

    const companyStatusKeys: string[] = workOrdersAnalytics?.company_status_keys ?? [];
    const byCompanyStatus = workOrdersAnalytics?.by_company_status ?? [];
    const companyStatusConfig = useMemo(() => {
        const config: Record<string, { label: string; color: string }> = {};
        companyStatusKeys.forEach((key) => {
            config[key] = { label: statusLabel(key), color: statusColor(STATUS_COLORS, key) };
        });
        return config;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyStatusKeys]);

    const completionsByEmployee = workOrdersAnalytics?.completions_by_employee ?? [];

    const createdByPieData = useMemo(
        () =>
            (workOrdersAnalytics?.created_by_distribution ?? []).map((row, i) => ({
                name: row.user,
                value: row.count,
                fill: categoricalColor(i),
            })),
        [workOrdersAnalytics],
    );
    const createdByChartConfig = useMemo(() => {
        const cfg: Record<string, { label: string; color: string }> = {};
        createdByPieData.forEach((entry) => {
            cfg[entry.name] = { label: entry.name, color: entry.fill };
        });
        return cfg;
    }, [createdByPieData]);

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold">{t('analytics.labels.page_5_work_orders_dashboard', 'Work Orders Analytics')}</h2>
                <p className="text-muted-foreground text-sm">
                    {t('analytics.labels.service_throughput', 'Service throughput, resolution speed, staffing and on-time delivery.')}
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
                <SummaryMetricCard
                    docKey="workorders.work_orders_created"
                    title={t('analytics.labels.work_orders_created', 'Work Orders Created')}
                    value={kpis.created}
                    subtitle={t('analytics.labels.selected_period', 'Selected period')}
                    icon={<ClipboardList className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="workorders.work_orders_completed"
                    title={t('analytics.labels.work_orders_completed', 'Work Orders Completed')}
                    value={kpis.completed}
                    subtitle={`${kpis.completionRate.toFixed(1)}% ${t('analytics.labels.completion_rate', 'completion rate')}`}
                    icon={<CheckCircle2 className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="workorders.avg_resolution_time"
                    title={t('analytics.labels.avg_resolution_time', 'Avg Resolution Time')}
                    value={`${kpis.avgResolutionDays.toFixed(1)}d`}
                    subtitle={t('analytics.labels.created_to_completed', 'Created to completed')}
                    icon={<Clock3 className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="workorders.on_time_rate"
                    title={t('analytics.labels.on_time_rate', 'On-Time Rate')}
                    value={`${kpis.onTimeRate.toFixed(1)}%`}
                    subtitle={t('analytics.labels.completed_by_estimated_date', 'Completed by estimated date')}
                    icon={<TrendingUp className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="workorders.avg_days_late"
                    title={t('analytics.labels.avg_days_late', 'Avg Days Late')}
                    value={`${kpis.avgDaysLate.toFixed(1)}d`}
                    subtitle={t('analytics.labels.late_orders_only', 'Late orders only')}
                    icon={<Timer className="h-4 w-4" />}
                />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {/* Created vs Completed — Area Chart */}
                <AnalyticsChartCard
                    docKey="workorders.created_vs_completed_trend"
                    title={t('analytics.labels.created_vs_completed_trend', 'Created vs Completed Trend')}
                >
                    <ChartContainer
                        role="img"
                        aria-label={t('analytics.labels.created_vs_completed_trend', 'Created vs Completed Trend')}
                        config={{
                            created: { label: t('analytics.labels.created', 'Created'), color: '#2563eb' },
                            completed: { label: t('analytics.labels.completed', 'Completed'), color: '#16a34a' },
                        }}
                        className={`${trendHeightClass} w-full`}
                    >
                        <AreaChart data={createdVsCompleted}>
                            <defs>
                                <linearGradient id="fillCreated" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.05} />
                                </linearGradient>
                                <linearGradient id="fillCompleted" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0.05} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                            <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} domain={[0, 'auto']} tick={{ fontSize: 11 }} />
                            <ChartTooltip content={<ChartTooltipContent />} />
                            <ChartLegend content={<ChartLegendContent />} />
                            <Area dataKey="created" type="monotone" stroke="#2563eb" strokeWidth={2} fill="url(#fillCreated)" />
                            <Area dataKey="completed" type="monotone" stroke="#16a34a" strokeWidth={2} fill="url(#fillCompleted)" />
                        </AreaChart>
                    </ChartContainer>
                </AnalyticsChartCard>

                {/* Work Orders by Status — Pie Chart */}
                <AnalyticsChartCard docKey="workorders.by_status" title={t('analytics.labels.work_orders_by_status', 'Work Orders by Status')}>
                    {statusPieData.length > 0 ? (
                        <ChartContainer
                            role="img"
                            aria-label={t('analytics.labels.work_orders_by_status', 'Work Orders by Status')}
                            config={statusChartConfig}
                            className={`${trendHeightClass} w-full`}
                        >
                            <PieChart>
                                <ChartTooltip content={<ChartTooltipContent nameKey="name" />} />
                                <Legend
                                    layout="vertical"
                                    align="right"
                                    verticalAlign="middle"
                                    iconType="circle"
                                    formatter={(value) => <span className="text-sm">{value}</span>}
                                />
                                <Pie data={statusPieData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={110} paddingAngle={2}>
                                    {statusPieData.map((entry, i) => (
                                        <Cell key={i} fill={entry.fill} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ChartContainer>
                    ) : (
                        <div className={`text-muted-foreground flex ${trendHeightClass} items-center justify-center text-sm`}>
                            {t('analytics.labels.no_work_orders_found', 'No work orders found')}
                        </div>
                    )}
                </AnalyticsChartCard>
            </div>

            {/* Overdue Work Orders — Count + Link */}
            <AnalyticsChartCard
                docKey="workorders.overdue"
                title={t('analytics.labels.overdue_work_orders', 'Overdue Work Orders')}
                contentClassName="flex items-center gap-6"
            >
                <div className="text-5xl font-bold">{overdueCount}</div>
                <div className="space-y-1">
                    <p className="text-muted-foreground text-sm">
                        {t('analytics.labels.work_orders_past_expected_date', 'Work orders past expected completion date in selected period.')}
                    </p>
                    <a href={overdueUrl} className="text-primary flex items-center gap-1 text-sm font-medium underline-offset-4 hover:underline">
                        <ExternalLink className="h-3.5 w-3.5" />
                        {t('analytics.labels.view_overdue_in_table', 'View in work orders table')}
                    </a>
                </div>
                {overdueCount === 0 && (
                    <Badge variant="outline" className="border-success text-success-subtle-foreground ml-auto">
                        {t('analytics.labels.no_overdue', 'All on time')}
                    </Badge>
                )}
            </AnalyticsChartCard>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {/* Work Orders by Company & Status */}
                {byCompanyStatus.length > 0 && (
                    <AnalyticsChartCard
                        docKey="workorders.by_company_status"
                        title={t('analytics.labels.work_orders_by_company_status', 'Work Orders by Company & Status')}
                        className="xl:col-span-2"
                    >
                        {/* Companies are discrete categories, so a stacked bar (not a line,
                                which would imply a trend between unrelated companies) is the correct encoding. */}
                        <ChartContainer
                            role="img"
                            aria-label={t('analytics.labels.work_orders_by_company_status', 'Work Orders by Company & Status')}
                            config={companyStatusConfig}
                            className={`${companyStatusHeightClass} w-full`}
                        >
                            <BarChart data={byCompanyStatus}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" vertical={false} />
                                <XAxis
                                    dataKey="company"
                                    angle={isMobile ? -60 : -25}
                                    textAnchor="end"
                                    height={isMobile ? 60 : 80}
                                    interval={isMobile ? 'preserveStartEnd' : 0}
                                    tickFormatter={truncateCompanyTick}
                                    tick={{ fontSize: 11 }}
                                />
                                <YAxis allowDecimals={false} domain={[0, 'auto']} tick={{ fontSize: 11 }} />
                                <ChartTooltip content={<ChartTooltipContent />} />
                                <ChartLegend content={<ChartLegendContent />} />
                                {companyStatusKeys.map((key) => (
                                    <Bar
                                        key={key}
                                        dataKey={key}
                                        name={statusLabel(key)}
                                        stackId="status"
                                        fill={statusColor(STATUS_COLORS, key)}
                                        radius={[0, 0, 0, 0]}
                                    />
                                ))}
                            </BarChart>
                        </ChartContainer>
                    </AnalyticsChartCard>
                )}

                {/* Completions per Employee */}
                {completionsByEmployee.length > 0 && (
                    <AnalyticsChartCard
                        docKey="workorders.completions_per_employee"
                        title={t('analytics.labels.completions_per_employee', 'Completions per Employee')}
                        contentClassName="space-y-2"
                    >
                        {completionsByEmployee.map((row, i) => (
                            <div key={row.employee ?? i} className="flex items-center justify-between rounded border p-2 text-sm">
                                <span>{row.employee}</span>
                                <Badge variant="secondary">{row.count}</Badge>
                            </div>
                        ))}
                    </AnalyticsChartCard>
                )}

                {/* Created By — Pie Chart */}
                <AnalyticsChartCard docKey="workorders.created_by" title={t('analytics.labels.work_orders_created_by', 'Created By')}>
                    {createdByPieData.length > 0 ? (
                        <ChartContainer
                            role="img"
                            aria-label={t('analytics.labels.work_orders_created_by', 'Created By')}
                            config={createdByChartConfig}
                            className={`${trendHeightClass} w-full`}
                        >
                            <PieChart>
                                <ChartTooltip content={<ChartTooltipContent nameKey="name" />} />
                                <Legend
                                    layout="vertical"
                                    align="right"
                                    verticalAlign="middle"
                                    iconType="circle"
                                    formatter={(value) => <span className="text-sm">{value}</span>}
                                />
                                <Pie data={createdByPieData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={110} paddingAngle={2}>
                                    {createdByPieData.map((entry, i) => (
                                        <Cell key={i} fill={entry.fill} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ChartContainer>
                    ) : (
                        <div className={`text-muted-foreground flex ${trendHeightClass} items-center justify-center text-sm`}>
                            {t('analytics.labels.no_work_orders_found', 'No work orders found')}
                        </div>
                    )}
                </AnalyticsChartCard>
            </div>
        </div>
    );
}
